/**
 * ============================================================================
 * BUDGET TRANSACTIONS v4.0 - DEBTS CARD LAYOUT
 * ============================================================================
 *
 * ✅ v4.0: DEBTS CARD LAYOUT
 *          - Card wrapper (white bg, border, rounded corners)
 *          - Card header: title left + add button right (wie .debts-card)
 *          - Filter row inside card (below header)
 *          - Transaction items: border-bottom separators (kein individuelle Karte)
 * ✅ v3.2: FILTER ROW PARALLEL
 * ✅ v3.1: BUTTON STRATEGY ALIGNMENT
 */

// ============================================================================
// STYLES
// ============================================================================
const styles = {
  // ── Card Container (wie .debts-card) ──────────────────────────────────────
  card: {
    background: 'white',
    borderRadius: '20px',
    border: '2px solid #B8D4DC',
    boxShadow: '0 2px 8px rgba(74, 144, 164, 0.1)',
    overflow: 'hidden'
  },

  // ── Card Header (wie .debts-card .card-header) ───────────────────────────
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.45rem 1.25rem',
    borderBottom: '1px solid #D4E8EC',
    background: '#E8F4F8'
  },
  cardTitle: {
    margin: 0,
    fontSize: '1.1rem',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    color: '#1F2937'
  },

  // ── Add Button (wie .btn-add in Debts.css) ───────────────────────────────
  // ── L1 Primary (52px, 14px radius) — einzige Hauptaktion pro Tab ──────────
  addButton: {
    padding: '0 28px',
    minHeight: '52px',
    background: 'linear-gradient(135deg, #4A90A4 0%, #6BA893 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '14px',
    fontSize: '0.95rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
    boxShadow: '0 4px 16px rgba(74, 144, 164, 0.25)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    lineHeight: 1,
    boxSizing: 'border-box',
    whiteSpace: 'nowrap'
  },

  // ── Filter Row (inside card) ──────────────────────────────────────────────
  filterSection: {
    padding: '0.875rem 1.5rem',
    borderBottom: '1px solid #D4E8EC',
    background: '#FAFCFE'
  },
  filterRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: '0.5rem'
  },
  filterSelect: {
    flex: 1,
    minWidth: 0,
    padding: '0.5rem 0.75rem',
    border: '2px solid #B8D4DC',
    borderRadius: '10px',
    fontSize: '0.82rem',
    background: '#F0F9FF',
    cursor: 'pointer',
    color: '#1F2937',
    fontWeight: 500,
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box'
  },

  // ── Stats Bar ─────────────────────────────────────────────────────────────
  statsBar: {
    display: 'flex',
    gap: '1.25rem',
    flexWrap: 'wrap',
    padding: '0.6rem 1.5rem',
    background: '#F0F9FF',
    borderBottom: '1px solid #D4E8EC',
    fontSize: '0.82rem'
  },
  statItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px'
  },

  // ── Empty State ───────────────────────────────────────────────────────────
  emptyState: {
    textAlign: 'center',
    padding: '3rem 2rem',
    color: '#6B7280'
  },
  emptyIcon: {
    fontSize: '3rem',
    marginBottom: '1rem'
  },
  emptyTitle: {
    margin: '0 0 0.5rem 0',
    color: '#1F2937',
    fontWeight: 600
  },
  emptyText: {
    margin: 0,
    fontSize: '0.9rem'
  },

  // ── Transaction Items (wie .debt-item) ────────────────────────────────────
  transactionsList: {
    padding: 0
  },
  transactionItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.55rem 1.25rem',
    borderBottom: '1px solid #D4E8EC',
    cursor: 'pointer',
    transition: 'background 0.15s'
  },

  // Icon box
  transactionIcon: {
    fontSize: '0.95rem',
    width: '30px',
    height: '30px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#E8F4F8',
    border: '1px solid #B8D4DC',
    borderRadius: '7px',
    flexShrink: 0
  },

  // Info area
  transactionInfo: {
    flex: 1,
    minWidth: 0
  },
  transactionName: {
    fontWeight: 600,
    color: '#1F2937',
    fontSize: '0.92rem',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  transactionMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '0.78rem',
    color: '#6B7280',
    marginTop: '2px'
  },
  recurringBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '2px',
    padding: '1px 6px',
    background: '#E8F4F8',
    borderRadius: '4px',
    fontSize: '0.7rem',
    color: '#4A90A4'
  },

  // Amount
  transactionAmount: {
    fontWeight: 700,
    fontSize: '0.95rem',
    whiteSpace: 'nowrap',
    flexShrink: 0
  },

  // Delete button (L4 Mini)
  deleteBtn: {
    width: '32px',
    height: '32px',
    padding: 0,
    background: 'transparent',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '1rem',
    opacity: 0.5,
    transition: 'all 0.2s',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    lineHeight: 1,
    boxSizing: 'border-box',
    flexShrink: 0
  },

  // Category chip on left border indicator
  categoryDot: {
    width: '4px',
    alignSelf: 'stretch',
    borderRadius: '2px',
    flexShrink: 0
  }
};

// ============================================================================
// COMPONENT
// ============================================================================
function BudgetTransactions({
  transactions,
  categories,
  filterType,
  setFilterType,
  filterCategory,
  setFilterCategory,
  dateRange,
  setDateRange,
  onEdit,
  onDelete,
  onAdd
}) {

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('de-DE') : '';
  const formatCurrency = (amount) => new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR'
  }).format(amount || 0);

  const getCategoryInfo = (name) => {
    const cat = categories.find(c => c.name === name);
    return cat || { name, icon: '💰', color: '#4A90A4' };
  };

  const getFilteredTransactions = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    let startDate = null;
    let endDate = now;

    switch (dateRange) {
      case 'currentWeek': {
        const dayOfWeek = today.getDay();
        const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        startDate = new Date(today);
        startDate.setDate(today.getDate() - mondayOffset);
        break;
      }
      case 'currentMonth': {
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      }
      case 'lastMonth': {
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      }
      case 'lastYear': {
        startDate = new Date(now.getFullYear() - 1, 0, 1);
        endDate = new Date(now.getFullYear() - 1, 11, 31);
        break;
      }
      case 'all':
      default:
        startDate = null;
        break;
    }

    return transactions.filter(tx => {
      const matchesType = filterType === 'all' || tx.type === filterType;
      const matchesCategory = filterCategory === 'all' || tx.category === filterCategory;
      const txDate = new Date(tx.date);
      const matchesDate = !startDate || (txDate >= startDate && txDate <= endDate);
      return matchesType && matchesCategory && matchesDate;
    });
  };

  const filtered = getFilteredTransactions();

  const stats = {
    total: filtered.length,
    income: filtered.filter(t => t.type === 'income').reduce((sum, t) => sum + parseFloat(t.amount || 0), 0),
    expense: filtered.filter(t => t.type === 'expense').reduce((sum, t) => sum + Math.abs(parseFloat(t.amount || 0)), 0)
  };

  return (
    <div style={styles.card}>

      {/* Card Header */}
      <div style={styles.cardHeader}>
        <h2 style={styles.cardTitle}>
          <span>📝</span> Transaktionen
        </h2>
      </div>

      {/* Filter Row */}
      <div style={styles.filterSection}>
        <div style={styles.filterRow}>
          {setDateRange && (
            <select
              value={dateRange || 'all'}
              onChange={(e) => setDateRange(e.target.value)}
              style={styles.filterSelect}
              onFocus={(e) => e.currentTarget.style.borderColor = '#4A90A4'}
              onBlur={(e) => e.currentTarget.style.borderColor = '#B8D4DC'}
            >
              <option value="currentWeek">Aktuelle Woche</option>
              <option value="currentMonth">Aktueller Monat</option>
              <option value="lastMonth">Letzter Monat</option>
              <option value="lastYear">Letztes Jahr</option>
              <option value="all">Alle Zeit</option>
            </select>
          )}

          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            style={styles.filterSelect}
            onFocus={(e) => e.currentTarget.style.borderColor = '#4A90A4'}
            onBlur={(e) => e.currentTarget.style.borderColor = '#B8D4DC'}
          >
            <option value="all">Alle Typen</option>
            <option value="income">💰 Einnahmen</option>
            <option value="expense">💸 Ausgaben</option>
          </select>

          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            style={styles.filterSelect}
            onFocus={(e) => e.currentTarget.style.borderColor = '#4A90A4'}
            onBlur={(e) => e.currentTarget.style.borderColor = '#B8D4DC'}
          >
            <option value="all">Alle Kategorien</option>
            {categories.map(c => (
              <option key={c.id} value={c.name}>{c.icon} {c.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Stats Bar */}
      {filtered.length > 0 && (
        <div style={styles.statsBar}>
          <div style={styles.statItem}>
            <span>📊</span>
            <span><strong>{stats.total}</strong> Einträge</span>
          </div>
          <div style={styles.statItem}>
            <span style={{ color: '#22C55E' }}>+{formatCurrency(stats.income)}</span>
          </div>
          <div style={styles.statItem}>
            <span style={{ color: '#EF4444' }}>-{formatCurrency(stats.expense)}</span>
          </div>
          <div style={styles.statItem}>
            <span style={{ color: stats.income - stats.expense >= 0 ? '#22C55E' : '#EF4444' }}>
              = {formatCurrency(stats.income - stats.expense)}
            </span>
          </div>
        </div>
      )}

      {/* Transactions List */}
      {filtered.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>📝</div>
          <h3 style={styles.emptyTitle}>Keine Transaktionen gefunden</h3>
          <p style={styles.emptyText}>
            {transactions.length === 0
              ? 'Erstelle deine erste Transaktion'
              : 'Ändere die Filter um mehr zu sehen'
            }
          </p>
          {onAdd && transactions.length === 0 && (
            <button
              style={{ ...styles.addButton, marginTop: '1rem' }}
              onClick={onAdd}
            >
              + Erste Transaktion erstellen
            </button>
          )}
        </div>
      ) : (
        <div style={styles.transactionsList}>
          {filtered.sort((a, b) => new Date(b.date) - new Date(a.date)).map((tx, idx, arr) => {
            const cat = getCategoryInfo(tx.category);
            const isLast = idx === arr.length - 1;
            return (
              <div
                key={tx.id}
                style={{
                  ...styles.transactionItem,
                  borderBottom: isLast ? 'none' : '1px solid #D4E8EC'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#F0F9FF'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                {/* Color indicator bar */}
                <div style={{ ...styles.categoryDot, background: cat.color }} />

                {/* Icon */}
                <div style={styles.transactionIcon}>{cat.icon}</div>

                {/* Info */}
                <div
                  style={styles.transactionInfo}
                  onClick={() => onEdit && onEdit(tx)}
                >
                  <div style={styles.transactionName}>
                    {tx.description || cat.name}
                  </div>
                  <div style={styles.transactionMeta}>
                    <span>{formatDate(tx.date)}</span>
                    <span>·</span>
                    <span>{cat.name}</span>
                    {tx.is_recurring && (
                      <span style={styles.recurringBadge}>🔄 Wiederkehrend</span>
                    )}
                  </div>
                </div>

                {/* Amount */}
                <div style={{
                  ...styles.transactionAmount,
                  color: tx.type === 'income' ? '#22C55E' : '#EF4444'
                }}>
                  {tx.type === 'income' ? '+' : '-'}{formatCurrency(Math.abs(tx.amount))}
                </div>

                {/* Delete */}
                {onDelete && (
                  <button
                    onClick={() => onDelete(tx.id)}
                    style={styles.deleteBtn}
                    className="budget-tx-delete-btn"
                    onMouseEnter={(e) => {
                      e.currentTarget.style.opacity = '1';
                      e.currentTarget.style.background = '#FEE2E2';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.opacity = '0.5';
                      e.currentTarget.style.background = 'transparent';
                    }}
                    title="Löschen"
                  >
                    🗑️
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Touch target for delete buttons */}
      <style>{`
        .budget-tx-delete-btn::before {
          content: '';
          position: absolute;
          inset: -6px;
        }
      `}</style>

    </div>
  );
}

export default BudgetTransactions;
